import { ApolloError } from '@apollo/client';

import {
  deleteAllWithSameProperties,
  useCascade,
  UseCascadeResult,
  useMutation,
  useTranslation,
} from '@gimlite/common';
import { WizardItemType, Zone } from '@gimlite/ui-v1';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Mutation,
  MutationCreateContractArgs,
} from '../../../../client/graphql';
import { createContractGQL } from '../../../../common/gql/createContract.gql';
import { convertCredentialsFrontToBack } from '../../../../common/mapper/credential.mapper';
import {
  ContractMotoristLine,
  statusHelper,
} from '../../../../composition/line/contract-motorist.line';
import { ContractWizardInfos } from '../../../../composition/wizard-infos/contract.wizard-infos';
import {
  SummaryForm,
  SummaryFormType,
} from '../../../../form/summary/summary.form';
import { AddOneMotoristToAnExistingPoolContractWizardContext } from '../../addOneMotoristToAnExistingPoolContract.wizard';

type ResultPosibility = any;
// | Mutation['createMotorist']
// | Mutation['createLocalMotorist'];

export const SummaryStep = ({
  context: {
    contractBase,
    contractMode,
    contractDetails,
    credentials,
    addFakeQRCODE,
    subPoolExistedSave,
    subPoolContractId,
    product: { id: productId },
    parkingId,
  },
  validate,
  submit,
}: WizardItemType.Config.Component<AddOneMotoristToAnExistingPoolContractWizardContext>) => {
  const { t, lang }: any = useTranslation();

  const [created, setCreated] = useState<
    SummaryFormType.Data.Line[] | undefined
  >(undefined);

  const [createContractCall] = useMutation<
    { createContract: Mutation['createContract'] },
    MutationCreateContractArgs
  >(createContractGQL);

  const { isFinish } = useCascade<ResultPosibility>([
    ...(contractMode === 'exist'
      ? [
          async () => {
            try {
              const response = await createContractCall({
                variables: {
                  input: {
                    parkingId: parkingId!,
                    productId: productId!,
                    startDate: contractBase.startDate!,
                    stopDate: contractBase.endDate!,
                    description: contractBase.description,
                    reference: contractBase.reference,
                    antiPassBackMode: contractBase.APBMode,
                    isEnabled: contractBase.state,
                    name1: contractDetails.exist!.name1,
                    name2: contractDetails.exist!.name2,
                    address:{
                       address1: contractDetails.exist?.address?.address1,
                       address2: contractDetails.exist?.address?.address2,
                       zipcode: contractDetails.exist?.address?.zipcode,
                       city: contractDetails.exist?.address?.city,
                       country: contractDetails.exist?.address?.country,
                    }
                    ,
                    lang: contractDetails.exist!.lang,
                    username: contractDetails.exist!.username,
                    sendInvitation: true,
                    articleCode: contractBase.articleCode!,
                    linkedContractId: subPoolContractId,
                    motoristId: contractDetails.exist!.motoristId,
                    shortUid: contractDetails.exist!.shortUid,
                    freeFlagIsEnabled:
                      contractBase.APBMode !== 'NONE'
                        ? !contractBase.APBNext
                        : null,
                    credentials:
                      (contractDetails.exist!.credentials || []).map(
                        ({ __typename, _id, ...rest }) => ({
                          ...rest,
                        }),
                      ) || [],
                  },
                },
              });

              if (
                response?.errors &&
                Array.isArray(response?.errors) &&
                response?.errors.length > 0
              ) {
                throw response?.errors[0]?.message;
              }

              const createContract = response.data?.createContract;

              setCreated(() => [
                {
                  _id: uuidv4(),
                  items: {
                    component: (
                      <ContractMotoristLine
                        data={{
                          firstName: createContract?.name1 || null,
                          lastName: createContract?.name2 || null,
                          contractId: createContract?.ospContractId || null,
                          status: statusHelper({
                            isEnabled: createContract?.isEnabled!,
                            startDate: new Date(createContract?.startDate!),
                            stopDate: new Date(createContract?.stopDate!),
                          }),
                        }}
                        config={{ action: false }}
                      />
                    ),
                    state: {
                      type: 'success',
                    },
                  },
                },
              ]);

              return { title: 'CreateContract', response: createContract };
            } catch (error: unknown) {
              setCreated(() => [
                {
                  _id: uuidv4(),
                  items: {
                    component: (
                      <ContractMotoristLine
                        data={{
                          firstName: contractDetails.exist!.name1 || null,
                          lastName: contractDetails.exist!.name2 || null,
                          contractId: null,
                          status: contractBase.state ? 'ACTIVE' : 'SUSPENDED',
                        }}
                        config={{ action: false }}
                      />
                    ),
                    state: {
                      type: 'error',
                      message:
                        error instanceof ApolloError
                          ? `${error.message}`
                          : `${error}`,
                    },
                  },
                },
              ]);

              return {
                title: 'CreateContract',
                response: new Error(
                  error instanceof ApolloError
                    ? `${error.message}`
                    : `${error}`,
                ),
              };
            }
          },
        ]
      : [
          async (prevResult: Array<UseCascadeResult<ResultPosibility>>) => {
            try {
              const response = await createContractCall({
                variables: {
                  input: {
                    parkingId: parkingId!,
                    productId: productId!,
                    startDate: contractBase.startDate!,
                    stopDate: contractBase.endDate!,
                    description: contractBase.description,
                    reference: contractBase.reference,
                    antiPassBackMode: contractBase.APBMode,
                    isEnabled: contractBase.state,
                    name1: contractDetails.create!.name1,
                    name2: contractDetails.create!.name2,
                    lang: contractDetails.create!.lang,
                    address:{
                       address1: contractDetails.create!.address1,
                       address2: contractDetails.create!.address2,
                       zipcode: contractDetails.create!.zipcode,
                       city: contractDetails.create!.city,
                       country: contractDetails.create!.country,
                    },
                    username: contractDetails.create!.username,
                    sendInvitation: contractDetails.create.inviteMotorist,
                    articleCode: contractBase.articleCode!,
                    linkedContractId: subPoolContractId,
                    freeFlagIsEnabled:
                      contractBase.APBMode !== 'NONE'
                        ? !contractBase.APBNext
                        : null,
                    credentials: convertCredentialsFrontToBack(
                      credentials.desactived.reduce(
                        (acc, credential) =>
                          deleteAllWithSameProperties(acc, credential),
                        addFakeQRCODE
                          ? credentials.actived.filter(
                              ({ name }) => name !== 'QRCODE',
                            )
                          : credentials.actived,
                      ),
                    ).map(({ _id, ...rest }) => ({
                      ...rest,
                    })),
                  },
                },
              });

              if (
                response?.errors &&
                Array.isArray(response?.errors) &&
                response?.errors.length > 0
              ) {
                throw response?.errors[0]?.message;
              }

              const createContract = response.data?.createContract;

              setCreated((list) => [
                ...(list ? list : []),
                {
                  _id: uuidv4(),
                  items: {
                    component: (
                      <ContractMotoristLine
                        data={{
                          firstName: createContract?.name1 || null,
                          lastName: createContract?.name2 || null,
                          contractId: createContract?.ospContractId || null,
                          status: statusHelper({
                            isEnabled: createContract?.isEnabled!,
                            startDate: new Date(createContract?.startDate!),
                            stopDate: new Date(createContract?.stopDate!),
                          }),
                        }}
                        config={{ action: false }}
                      />
                    ),
                    state: {
                      type: 'success',
                    },
                  },
                },
              ]);

              return { title: 'CreateContract', response: createContract };
            } catch (error: unknown) {
              setCreated((list) => [
                ...(list ? list : []),
                {
                  _id: uuidv4(),
                  items: {
                    component: (
                      <ContractMotoristLine
                        data={{
                          firstName: contractDetails.create.name1,
                          lastName: contractDetails.create.name2,
                          contractId: null,
                          status: contractBase.state ? 'ACTIVE' : 'SUSPENDED',
                        }}
                        config={{ action: false }}
                      />
                    ),
                    state: {
                      type: 'error',
                      message:
                        error instanceof ApolloError
                          ? `${error.message}`
                          : `${error}`,
                    },
                  },
                },
              ]);

              return {
                title: 'CreateContract',
                response: new Error(
                  error instanceof ApolloError
                    ? `${error.message}`
                    : `${error}`,
                ),
              };
            }
          },
        ]),
  ]);

  useEffect(() => {
    validate(isFinish);
  }, [isFinish]);

  return (
    <Zone
      config={{
        gap: {
          y: 1,
          x: 1,
        },
        zones: [['info', 'list']],
        rows: ['1fr'],
        columns: ['min-content', '1fr'],
      }}
    >
      <Zone.Area config={{ area: 'info' }}>
        <ContractWizardInfos
          data={{
            APBMode: contractBase.APBMode,
            APBNext:
              contractBase.APBMode !== 'NONE' ? contractBase.APBNext : null,
            state: contractBase.state,
            startDate: contractBase.startDate,
            endDate: contractBase.endDate,
            description: contractBase.description,
            reference: contractBase.reference,
            articleCode: contractBase.articleCode,
          }}
        />
      </Zone.Area>

      <Zone.Area config={{ area: 'list' }}>
        <SummaryForm
          data={{
            list: created,
          }}
        />
      </Zone.Area>
    </Zone>
  );
};
